import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { ArrowForward } from "@remo-co/ui-core/src/icons/ArrowForward";
import { Lock as LockIcon } from "@remo-co/ui-core/src/icons/Lock";
import { push } from "connected-react-router";
import { useI18n } from "i18n";
import { addDialogNotification } from "modules/dialogNotification/redux/dialogNotificationSlice";
import useEnterLeaveEvent from "modules/event/hooks/useEnterLeaveEvent";
import { IEvent } from "modules/event/types";
import { DNS_KEYS } from "modules/globalUserPreference";
import { Actions, trackAction } from "modules/monitoring";
import { selectCurrentUserPreferences } from "modules/userPreferences/redux/selectors";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { useStyles } from "./styles";

interface Props {
  event: Partial<IEvent>;
  currentEventId: string;
}

const { disableSwitchEventNotification } = DNS_KEYS;

const EventCard = ({ event, currentEventId }: Props) => {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useI18n(["eventDirectory", "event"]);
  const { leaveEvent } = useEnterLeaveEvent(currentEventId);
  const currentUserPreferences = useSelector(selectCurrentUserPreferences);

  const isDNSDisabled = useCallback(
    () =>
      !(
        currentUserPreferences &&
        currentUserPreferences[disableSwitchEventNotification]
      ),
    [currentUserPreferences],
  );

  const navigateToEvent = () => {
    leaveEvent();

    trackAction(Actions.DIRECTORY_EVENT_CLICK, { event });
    dispatch(push(`/e/${event.code}?autojoin=1`));
  };

  const onEventClick = () => {
    if (isDNSDisabled()) {
      dispatch(
        addDialogNotification({
          message: t("eventDirectory:go.to.event.confirm", {
            name: event.name,
          }),
          hasDoNotShowAgain: true,
          isDoNotShowGlobal: true,
          dnsKey: disableSwitchEventNotification,
          title: t("eventDirectory:join.related.events"),
          confirmText: t("eventDirectory:go.to.event"),
          confirmTestId: "confirm-go-to-event",
          onConfirm: navigateToEvent,
          dismissText: t("eventDirectory:button.cancel"),
          className: styles.dialog,
        }),
      );
    } else {
      navigateToEvent();
    }
  };

  return (
    <Container padding="xs" fullWidth>
      <Container
        flex
        justifyContent="space-between"
        padding="xs"
        className={styles.eventContainer}
        onClick={onEventClick}
      >
        <Container flex justifyContent="flex-start" alignItems="flex-start">
          <img className={styles.eventLogo} src={event.logoURL} alt="" />
          <div className={styles.eventInfoContainer}>
            {event.isPrivate && <LockIcon />}
            <Typography variant="h3" className={styles.eventInfo}>
              {event.name}
            </Typography>
            <Typography variant="body1" className={styles.eventInfo}>
              {event.shortDescription}
            </Typography>
          </div>
        </Container>
        <Button variant="text" className={styles.enterButton}>
          {t("event:top.bar.enter")}
          <ArrowForward className={styles.arrowIcon} />
        </Button>
      </Container>
    </Container>
  );
};

export default EventCard;
